import { useTranslation } from 'react-i18next'
import { PageTitle } from '../components'
import { Outlet, useLocation, useNavigate } from '@tanstack/react-router'
import { DataManagementHeaderSlotProvider } from '@/features/DataManagement/components/DataManagementHeaderSlot/DataManagementHeaderSlotProvider'
import { ReportHeader } from '@/features/Reporting/components/ReportHeader/ReportHeader'
import { RoleRestrictedContent } from '@/components/molecules/RoleRestrictedContent/RoleRestrictedContent'
import { UserType } from '@/hooks'
import { DataManagementTabs } from '@/features/DataManagement/components/DataManagementTabs/DataManagementTabs'
import { useDataManagementHeaderSlot } from '@/features/DataManagement/components/DataManagementHeaderSlot/useDataManagementHeaderSlot'
import { useFeatureFlag } from '@/components/providers/FeatureFlagsProvider/useFeatureFlag'
import { ErrorState, LoadingState } from '@/components'
import { RoleBasedPlaceholder } from '@/components/organisms/RoleBasedPlaceholder/RoleBasedPlaceholder'
import { ReportRestrictedContent } from './ReportRestrictedContent'
import { useEffect } from 'react'
import { useReportAccess } from '@/hooks/useReportAccess'
import { PremiumOverviewContainer } from '@/features/DataManagement/components/PremiumOverviewContainer/PremiumOverviewContainer'

const energySourcingAndContractsPath =
    '/data-management/energy-sourcing-and-contracts'

const DataManagement = () => {
    const { t } = useTranslation('translations')
    const { slot } = useDataManagementHeaderSlot()
    const { pathname } = useLocation()
    const navigate = useNavigate()

    const title = t('sidebar_menu.data_management')

    const {
        isLoading,
        isFetching,
        isFetched,
        isError,
        error,
        isAgentOrBrokerWithoutCustomer,
        shouldShowRestrictedContent: shouldShowPremiumOverview,
    } = useReportAccess()

    const isDataManagementEnabled = useFeatureFlag('dataManagement')
    const isIndexRoute = pathname === '/data-management'
    const isEnergySourcingAndContractsPath =
        pathname === energySourcingAndContractsPath || isIndexRoute

    // Navigate to the first tab when showing PremiumOverview and on the index route
    useEffect(() => {
        if (shouldShowPremiumOverview && isFetched && isIndexRoute) {
            void navigate({
                to: energySourcingAndContractsPath,
            })
        }
    }, [shouldShowPremiumOverview, isFetched, isIndexRoute, navigate])

    if (!isDataManagementEnabled) {
        return null
    }

    if (isError) {
        if (
            error &&
            'status' in error &&
            typeof error.status === 'number' &&
            403 === error.status
        ) {
            return (
                <div className="grid h-full px-6 pb-6">
                    <ReportRestrictedContent />
                </div>
            )
        }

        return (
            <ErrorState
                className="h-full"
                description={t('common.error_state.description')}
                boxed={false}
            />
        )
    }

    if (isAgentOrBrokerWithoutCustomer) {
        return (
            <div className="grid h-full grow grid-cols-[100%] grid-rows-[auto_1fr]">
                <PageTitle
                    label={title}
                    labelTestId="page-title-data-management"
                    className="!pr-2"
                />

                <RoleBasedPlaceholder />
            </div>
        )
    }

    if (isLoading || isFetching || !isFetched) {
        return <LoadingState message={t('common.loading')} size="small" />
    }

    return (
        <div className="grid h-full grow grid-cols-[100%] grid-rows-[auto_1fr]">
            <div className="space-y-6">
                <div>
                    <ReportHeader
                        title={title}
                        renderTitle={() => (
                            <PageTitle
                                label={title}
                                labelTestId="page-title-data-management"
                            />
                        )}
                    >
                        {slot}
                    </ReportHeader>

                    <RoleRestrictedContent
                        allowedRoles={[UserType.CUSTOMER]}
                        strict={false}
                    >
                        <DataManagementTabs />
                    </RoleRestrictedContent>
                </div>
            </div>

            {shouldShowPremiumOverview && isEnergySourcingAndContractsPath ? (
                <PremiumOverviewContainer />
            ) : (
                <Outlet />
            )}
        </div>
    )
}

const DataManagementWithContext = () => (
    <DataManagementHeaderSlotProvider>
        <DataManagement />
    </DataManagementHeaderSlotProvider>
)

export default DataManagementWithContext
